import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import superjson from 'superjson';
import type { CredentialPromptState } from '../helpers/useMissingCredentialPrompt';
import { postSecret } from '../endpoints/taskingbot/secrets_POST.schema';

interface MissingCredentialModalProps {
  prompt: CredentialPromptState | null;
  onClose: () => void;
  /** Called after credentials are successfully saved to DB. Use to auto-retry the original message. */
  onSaved?: (integration: string) => void;
}

export const MissingCredentialModal: React.FC<MissingCredentialModalProps> = ({
  prompt,
  onClose,
  onSaved,
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset values when prompt changes
  useEffect(() => {
    setValues(prompt?.values || {});
    setError(null);
  }, [prompt?.integration]);

  if (!prompt || !prompt.fields || prompt.fields.length === 0) return null;

  const integrationLabel = prompt.integrationLabel || prompt.integration;

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);
    const integrationName = prompt.integration;
    console.log(
      '[CredentialModal] Saving credentials for:',
      integrationName,
      'fields:',
      Object.keys(values),
    );
    try {
      const trimmedValues = Object.fromEntries(
        Object.entries(values).filter(([, value]) => typeof value === 'string' && value.trim()),
      );
      const groupedValues = Object.entries(trimmedValues).reduce(
        (acc, [rawKey, value]) => {
          const [maybeIntegration, maybeField] = rawKey.includes('__')
            ? rawKey.split(/__(.+)/)
            : [prompt.integration, rawKey];
          const integrationKey = (maybeIntegration || prompt.integration).trim();
          const fieldKey = (maybeField || rawKey).trim();
          if (!acc[integrationKey]) acc[integrationKey] = {};
          acc[integrationKey][fieldKey] = String(value).trim();
          return acc;
        },
        {} as Record<string, Record<string, string>>,
      );
      const secretEntries = Object.entries(groupedValues).flatMap(([integrationKey, config]) => {
        const integrationSecretPrefix = integrationKey.replace(/[^a-z0-9]+/gi, '_').toUpperCase();
        return Object.entries(config).map(([key, value]) => ({
          label: `${integrationSecretPrefix}_${key.replace(/[^a-z0-9]+/gi, '_').toUpperCase()}`,
          value,
        }));
      });
      if (secretEntries.length > 0) {
        await Promise.all(secretEntries.map((entry) => postSecret(entry)));
      }
      for (const [groupIntegration, config] of Object.entries(groupedValues)) {
        const payload = JSON.stringify({
          integration: groupIntegration,
          config,
        });
        console.log(
          '[CredentialModal] POST /_api/taskingbot/credentials payload length:',
          payload.length,
        );

        const res = await fetch('/_api/taskingbot/credentials', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: payload,
        });

        console.log('[CredentialModal] Response status:', res.status);

        if (!res.ok) {
          const text = await res.text();
          console.error('[CredentialModal] Save failed:', res.status, text);
          let errorMsg = 'Failed to save credentials';
          try {
            const data = superjson.parse<{ error: string }>(text);
            errorMsg = data.error || errorMsg;
          } catch {
            try {
              const jsonObj = JSON.parse(text);
              errorMsg = jsonObj.error || errorMsg;
            } catch {
              errorMsg = text || errorMsg;
            }
          }
          throw new Error(errorMsg);
        }
      }

      console.log('[CredentialModal] Credentials saved successfully for:', integrationName);
      toast.success(`${integrationLabel} credentials saved and remembered — retrying...`);
      onClose();
      if (onSaved) onSaved(integrationName);
    } catch (err: any) {
      console.error('[CredentialModal] Save error:', err);
      const errMsg = err?.message || 'Failed to save credentials';
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const allFieldsFilled = prompt.fields.every((f) => values[f.key]?.trim());

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: 'var(--card, #1a1a2e)',
          border: '1px solid var(--border, rgba(255,255,255,0.08))',
          borderRadius: 'var(--radius, 12px)',
          maxWidth: '420px',
          width: '100%',
          margin: '0 16px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border, rgba(255,255,255,0.06))',
          }}
        >
          <Lock size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          <h2 style={{ fontSize: '0.95rem', fontWeight: 600, flex: 1 }}>
            {integrationLabel} credentials required
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              opacity: 0.5,
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <p style={{ fontSize: '0.8rem', opacity: 0.55, margin: 0 }}>
            This action requires {integrationLabel} credentials. Your data is encrypted and stored
            securely — only you can access it.
          </p>

          {prompt.fields.map((field) => (
            <div key={field.key}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  opacity: 0.7,
                  marginBottom: '6px',
                }}
              >
                {field.label}
              </label>
              <input
                type={field.type === 'password' ? 'password' : 'text'}
                placeholder={field.placeholder}
                value={values[field.key] || ''}
                onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '0.82rem',
                  color: 'inherit',
                  background:
                    'color-mix(in srgb, var(--secondary, hsl(220 10% 25%)) 25%, transparent)',
                  border: '1px solid var(--border, rgba(255,255,255,0.08))',
                  borderRadius: 'var(--radius-sm, 8px)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          ))}

          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'color-mix(in srgb, var(--error, #ef4444) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--error, #ef4444) 25%, transparent)',
                borderRadius: 'var(--radius-sm, 8px)',
                fontSize: '0.78rem',
                color: 'var(--error, #ef4444)',
              }}
            >
              {error}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.65rem',
              opacity: 0.35,
            }}
          >
            <AlertCircle size={12} />
            Encrypted with AES-256 — stored per-user, never shared
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            gap: '10px',
            padding: '16px 20px',
            borderTop: '1px solid var(--border, rgba(255,255,255,0.06))',
          }}
        >
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              flex: 1,
              padding: '8px 16px',
              fontSize: '0.78rem',
              fontWeight: 500,
              color: 'inherit',
              background: 'transparent',
              border: '1px solid var(--border, rgba(255,255,255,0.15))',
              borderRadius: 'var(--radius-sm, 8px)',
              cursor: 'pointer',
              opacity: isSaving ? 0.4 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!allFieldsFilled || isSaving}
            style={{
              flex: 1,
              padding: '8px 16px',
              fontSize: '0.78rem',
              fontWeight: 500,
              color: !allFieldsFilled || isSaving ? 'inherit' : '#080c16',
              background: !allFieldsFilled || isSaving ? 'transparent' : 'var(--primary)',
              border: '1px solid',
              borderColor:
                !allFieldsFilled || isSaving
                  ? 'var(--border, rgba(255,255,255,0.08))'
                  : 'var(--primary)',
              borderRadius: 'var(--radius-sm, 8px)',
              cursor: !allFieldsFilled || isSaving ? 'not-allowed' : 'pointer',
              opacity: !allFieldsFilled || isSaving ? 0.4 : 1,
            }}
          >
            {isSaving ? 'Saving...' : 'Save Securely'}
          </button>
        </div>
      </div>
    </div>
  );
};
